import React from 'react';
import moment from 'moment';

const formatHours = (decimalHours) => {
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  const sign = isNegative ? '-' : '';
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}h`;
};

// Helper function to format percentage with color
const formatPercentage = (percent) => {
  const isPositive = percent >= 0;
  const color = isPositive ? 'text-green-600' : 'text-red-600';
  const sign = isPositive ? '+' : '';
  return <span className={color}>{sign}{percent.toFixed(1)}%</span>;
};

export default function MobileFooter({ 
  totalsCurrentView, 
  totalsCurrentViewPeople,
  totalsYTD, 
  currentView, 
  currentDate,
  customRangeData,
  isLoadingCustomRange,
  onCustomDateRangeClick,
  onClearCustomRange,
  enablePeople,
  totalsYTDPeople
}) {

  // NEW: determine which context owns the active custom range
  const customContext = customRangeData?.range?.context || null;

  // Helper to render custom range data
  const renderCustomRangeRow = (isDesktop = false) => {
    if (!customRangeData) return null;

    const current = customRangeData.current;
    const previous = customRangeData.previous;
    const differences = customRangeData.differences;
    const hasComparison = previous && differences;

    return (
      <div className={`flex items-center ${isDesktop ? 'gap-2' : 'justify-between'}`}>
        <div className="flex items-center gap-2">
          <button
            onClick={onClearCustomRange}
            className={`${
              isDesktop ? 'w-28' : 'w-24'
            } px-2 py-1 bg-[#205799] hover:bg-[#273C61] text-white text-${isDesktop ? 'sm' : 'xs'} font-bold text-center rounded transition-colors`}
            title="Click to remove custom range"
          >
            {customRangeData.label}
          </button>
          {isDesktop && (
            <span className="text-xs text-gray-500">
              ({moment(customRangeData.range.start).format('D MMM')} - {moment(customRangeData.range.end).format('D MMM')})
            </span>
          )}
        </div>
        
        <div className={`${isDesktop ? 'text-sm' : 'text-xs'}`}>
          {isDesktop ? (
            // Desktop: Single line with full info
            <span>
              <strong>{formatHours(current.total)}</strong>
              {' '}({formatHours(current.billable)} bill.)
              {hasComparison && (
                <span className="text-gray-600">
                  {' '}| vs last year: {differences.total >= 0 ? '+' : ''}{formatHours(differences.total)} ({formatPercentage(differences.totalPercent)}) - was {formatHours(previous.total)}
                </span>
              )}
            </span>
          ) : (
            // Mobile: Compact two-line format
            <div>
              <div>
                <strong>{formatHours(current.total)}</strong>
                {' '}({formatHours(current.billable)} bill.)
              </div>
              {hasComparison && (
                <div className="text-gray-600 mt-0.5">
                  vs last year: {differences.total >= 0 ? '+' : ''}{formatHours(differences.total)} ({formatPercentage(differences.totalPercent)}) | was {formatHours(previous.total)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Footer - Compact - Fixed over address bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-3 bg-gray-50 border-t border-gray-200 z-[2000]">
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="px-2 py-1 bg-gray-200 text-black text-xs font-bold rounded">
              {currentView === 'day' ? moment(currentDate).format('D ddd') : 'Current View'}
            </span>
            <span className="text-xs">
              <strong>{formatHours(totalsCurrentView.billable + totalsCurrentView.nonBillable)}</strong> 
              {' '}({formatHours(totalsCurrentView.billable)} bill.)
            </span>
          </div>

          {/* NEW: Current View People (only for ZRM and when there is People time) */}
          {enablePeople && totalsCurrentViewPeople && (totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable) > 0 && (
            <div className="flex items-center justify-between">
              <span className="px-2 py-1 bg-gray-200 text-black text-xs font-bold rounded">
                Current View People
              </span>
              <span className="text-xs">
                <strong>{formatHours(totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable)}</strong>
                {' '}({formatHours(totalsCurrentViewPeople.billable)} bill.)
              </span>
            </div>
          )}

          {/* Custom Range or YTD (Projects only) */}
          {customRangeData && customContext === 'projects' ? (
            renderCustomRangeRow(false)
          ) : (
            <div className="flex items-center justify-between">
              <button
                onClick={() => onCustomDateRangeClick('projects')}
                className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-black text-xs font-bold rounded transition-colors"
                title="Click to select custom period"
              >
                Year-to-Date
              </button>
              <span className="text-xs">
                <strong>{formatHours(totalsYTD.billable + totalsYTD.nonBillable)}</strong>
                {' '}({formatHours(totalsYTD.billable)} bill.)
              </span>
            </div>
          )}

          {/* NEW: YTD People (mobile) OR Custom Range (People) */}
          {enablePeople && (
            customRangeData && customContext === 'people'
              ? renderCustomRangeRow(false)
              : (totalsYTDPeople && (totalsYTDPeople.billable + totalsYTDPeople.nonBillable) > 0 && (
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => onCustomDateRangeClick('people')}
                      className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-black text-xs font-bold rounded transition-colors"
                      title="Click to select custom period (People)"
                    >
                      YTD People
                    </button>
                    <span className="text-xs">
                      <strong>{formatHours(totalsYTDPeople.billable + totalsYTDPeople.nonBillable)}</strong>
                      {' '}({formatHours(totalsYTDPeople.billable)} bill.)
                    </span>
                  </div>
                ))
          )}
        </div>
      </div>

      {/* Tablet Footer - Medium - Fixed */}
      <div className="hidden md:block lg:hidden fixed bottom-0 left-0 right-0 p-3 bg-gray-50 border-t border-gray-200 z-[2000]">
        <div className="flex items-center gap-4 text-sm whitespace-nowrap overflow-x-auto">
          {/* Current View */}
          <div className="flex items-center gap-3">
            <span className="inline-flex w-auto shrink-0 whitespace-nowrap px-2 py-1 bg-gray-200 text-black text-sm font-bold text-center rounded">
              {currentView === 'day' ? moment(currentDate).format('D ddd') : 'Current'}
            </span>
            <span>
              Total: <strong>{formatHours(totalsCurrentView.billable + totalsCurrentView.nonBillable)}</strong> - 
              Billable: <strong>{formatHours(totalsCurrentView.billable)}</strong>
            </span>
          </div>

          {/* Current View People */}
          {enablePeople && totalsCurrentViewPeople && (totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable) > 0 && (
            <div className="flex items-center gap-3">
              <span className="inline-flex w-auto shrink-0 whitespace-nowrap px-2 py-1 bg-gray-200 text-black text-sm font-bold text-center rounded">
                People
              </span>
              <span>
                Total: <strong>{formatHours(totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable)}</strong> - 
                Billable: <strong>{formatHours(totalsCurrentViewPeople.billable)}</strong>
              </span>
            </div>
          )}

          {/* Year-to-Date (Projects) OR Custom Range (Projects) */}
          {customRangeData && customContext === 'projects'
            ? renderCustomRangeRow(true)
            : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => onCustomDateRangeClick('projects')}
                  className="w-24 px-2 py-1 bg-gray-200 hover:bg-gray-300 text-black text-sm font-bold text-center rounded transition-colors"
                  title="Click to select custom period"
                >
                  YTD
                </button>
                <span>
                  <strong>{formatHours(totalsYTD.billable + totalsYTD.nonBillable)}</strong>
                  {' '}({formatHours(totalsYTD.billable)} bill.)
                </span>
              </div>
            )
          }

          {/* YTD People OR Custom Range (People) */}
          {enablePeople && (
            customRangeData && customContext === 'people'
              ? renderCustomRangeRow(true)
              : (totalsYTDPeople && (totalsYTDPeople.billable + totalsYTDPeople.nonBillable) > 0 && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => onCustomDateRangeClick('people')}
                      className="w-24 px-2 py-1 bg-gray-200 hover:bg-gray-300 text-black text-sm font-bold text-center rounded transition-colors"
                      title="Click to select custom period (People)"
                    >
                      YTD People
                    </button>
                    <span>
                      Total: <strong>{formatHours(totalsYTDPeople.billable + totalsYTDPeople.nonBillable)}</strong> - 
                      Billable: <strong>{formatHours(totalsYTDPeople.billable)}</strong>
                    </span>
                  </div>
                ))
          )}

          {isLoadingCustomRange && (
            <div className="text-xs text-gray-500">
              Calculating custom period...
            </div>
          )}
        </div>
      </div>

      {/* Desktop Footer - Wide - Fixed */}
      <div className="hidden lg:block fixed bottom-0 left-0 right-0 p-3 bg-gray-50 border-t border-gray-200 z-[2000]">
        <div className="flex items-center gap-6 text-sm whitespace-nowrap">
          {/* Current View */}
          <div className="flex items-center gap-2">
            <span className="inline-flex w-auto shrink-0 whitespace-nowrap px-2 py-1 bg-gray-200 text-black text-sm font-bold text-center rounded">
              Current View
            </span>
            <span className="text-sm">
              <span className="text-sm font-bold">{currentView === 'day' && `${moment(currentDate).format('D ddd')} - `}</span>
              Total: <strong>{formatHours(totalsCurrentView.billable + totalsCurrentView.nonBillable)}</strong> - Billable: <strong>{formatHours(totalsCurrentView.billable)}</strong>
            </span>
          </div>

          {/* Current View People */}
          {enablePeople && totalsCurrentViewPeople && (totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable) > 0 && (
            <div className="flex items-center gap-2">
              <span className="inline-flex w-auto shrink-0 whitespace-nowrap px-2 py-1 bg-gray-200 text-black text-sm font-bold text-center rounded">
                Current View People
              </span>
              <span className="text-sm">
                Total: <strong>{formatHours(totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable)}</strong> - Billable: <strong>{formatHours(totalsCurrentViewPeople.billable)}</strong>
              </span>
            </div>
          )}

          {/* Year-to-Date (Projects) OR Custom Range (Projects) */}
          {customRangeData && customContext === 'projects'
            ? renderCustomRangeRow(true)
            : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onCustomDateRangeClick('projects')}
                  className="w-28 px-2 py-1 bg-gray-200 hover:bg-gray-300 text-black text-sm font-bold text-center transition-colors"
                  title="Click to select custom period and compare with last year"
                >
                  Year-to-Date
                </button>
                <span className="text-sm">
                  Total: <strong>{formatHours(totalsYTD.billable + totalsYTD.nonBillable)}</strong> - Billable: <strong>{formatHours(totalsYTD.billable)}</strong>
                </span>
              </div>
            )
          }

          {/* YTD People OR Custom Range (People) */}
          {enablePeople && (
            customRangeData && customContext === 'people'
              ? renderCustomRangeRow(true)
              : (totalsYTDPeople && (totalsYTDPeople.billable + totalsYTDPeople.nonBillable) > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onCustomDateRangeClick('people')}
                      className="w-28 px-2 py-1 bg-gray-200 hover:bg-gray-300 text-black text-sm font-bold text-center rounded transition-colors"
                      title="Click to select custom period (People)"
                    >
                      YTD People
                    </button>
                    <span>
                      Total: <strong>{formatHours(totalsYTDPeople.billable + totalsYTDPeople.nonBillable)}</strong> - 
                      Billable: <strong>{formatHours(totalsYTDPeople.billable)}</strong>
                    </span>
                  </div>
                ))
          )}

          {isLoadingCustomRange && (
            <div className="text-sm text-gray-500">
              Calculating custom period...
            </div>
          )}
        </div>
      </div>
    </>
  );
}